<template>
  <v-container class="register-exists-page py-8">
    <v-row justify="center">
      <v-col cols="12" lg="9" xl="8">
        <div class="exists-hero">
          <div class="exists-hero__icon" aria-hidden="true">
            <v-icon icon="mdi-account-check" size="40" />
          </div>
          <h1 class="exists-hero__title">
            Diese E-Mail ist bereits registriert
          </h1>
          <p class="exists-hero__lead">
            Mit der angegebenen E-Mail-Adresse gibt es bereits ein Konto bei
            <strong>TeamWaizmann</strong>. Sie können sich direkt anmelden, Ihr
            Passwort zurücksetzen oder uns kontaktieren.
          </p>
          <p v-if="emailDisplay" class="exists-hero__email">
            <span class="exists-hero__email-label">E-Mail</span>
            {{ emailDisplay }}
          </p>
        </div>

        <v-row class="exists-options" align="stretch">
          <v-col cols="12" md="4">
            <v-card class="exists-card exists-card--primary" elevation="2" rounded="lg">
              <v-card-text>
                <div class="exists-card__step">1</div>
                <h2 class="exists-card__title">Anmelden</h2>
                <p class="exists-card__text">
                  Melden Sie sich mit Ihrer E-Mail und Ihrem Passwort an.
                </p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="exists-card" elevation="2" rounded="lg">
              <v-card-text>
                <div class="exists-card__step exists-card__step--muted">2</div>
                <h2 class="exists-card__title">Passwort vergessen</h2>
                <p class="exists-card__text">
                  Im Formular unten auf „Passwort vergessen“ klicken — wir senden
                  Ihnen ein neues Passwort per E-Mail.
                </p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card class="exists-card" elevation="2" rounded="lg">
              <v-card-text>
                <div class="exists-card__step exists-card__step--muted">3</div>
                <h2 class="exists-card__title">Hilfe vom Team</h2>
                <p class="exists-card__text">
                  Wenn etwas nicht klappt, schreiben Sie uns — wir helfen gern weiter.
                </p>
                <v-btn
                  class="exists-mail-btn mt-2"
                  variant="outlined"
                  color="primary"
                  block
                  :href="mailtoSupport"
                >
                  <v-icon start icon="mdi-email-outline" />
                  info@teamwaizmann.de
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <div class="register-panel exists-login-panel mt-6">
          <h2 class="exists-login-heading">Login</h2>
          <LoginView
            :new-route="afterLoginRoute"
            :prefill-email="prefillEmail"
            compact-mode
          />
        </div>

        <p class="exists-back text-center mt-8">
          <router-link class="exists-back-link" :to="backRegisterPath">
            Zurück zur Registrierung
          </router-link>
        </p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, inject, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import LoginView from '@/components/objects/user/LoginView.vue'

const route = useRoute()
const updateDocumentHead = inject('updateDocumentHead')

const afterLoginRoute = '/waizmanntabellen_bestellen'

const prefillEmail = computed(() => {
  const q = route.query.email
  if (q === undefined || q === null) return ''
  return String(q).trim()
})

const emailDisplay = computed(() => prefillEmail.value)

const mailtoSupport = computed(() => {
  const subject = encodeURIComponent('TeamWaizmann – Zugang / Registrierung')
  const body = encodeURIComponent(
    prefillEmail.value
      ? `Meine E-Mail: ${prefillEmail.value}\n\n`
      : ''
  )
  return `mailto:info@teamwaizmann.de?subject=${subject}&body=${body}`
})

const backRegisterPath = computed(() => {
  const id = route.query.reg
  if (id !== undefined && id !== null && String(id) !== '') {
    return `/register/${id}`
  }
  return '/register'
})

onMounted(() => {
  updateDocumentHead(
    'TeamWaizmann | Konto bereits vorhanden',
    'Diese E-Mail ist bereits bei TeamWaizmann registriert — hier anmelden oder Passwort zurücksetzen.'
  )
})
</script>

<style scoped>
.register-exists-page {
  max-width: 1100px;
  margin: 0 auto;
}

.exists-hero {
  text-align: center;
  padding: 28px 20px 32px;
  background: linear-gradient(165deg, #f7f9fc 0%, #eef3fa 100%);
  border-radius: 16px;
  border: 1px solid #e1e6ef;
  margin-bottom: 28px;
}

.exists-hero__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(49, 102, 165, 0.12);
  color: #3166a5;
  margin-bottom: 16px;
}

.exists-hero__title {
  font-size: 1.55rem;
  font-weight: 600;
  color: #1f3a63;
  line-height: 1.25;
  margin: 0 0 12px;
}

.exists-hero__lead {
  max-width: 560px;
  margin: 0 auto;
  color: #4a5568;
  font-size: 0.98rem;
  line-height: 1.5;
}

.exists-hero__lead strong {
  color: #3166a5;
  font-weight: 600;
}

.exists-hero__email {
  margin: 18px auto 0;
  display: inline-block;
  padding: 10px 18px;
  background: #fff;
  border-radius: 999px;
  border: 1px solid #d8e0eb;
  font-size: 0.95rem;
  color: #1f3a63;
}

.exists-hero__email-label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #3166a5;
  font-weight: 600;
  margin-bottom: 2px;
}

.exists-options {
  margin-top: 4px;
}

.exists-card {
  height: 100%;
  border: 1px solid #e1e6ef;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.exists-card:hover {
  border-color: #c5d3e6;
  box-shadow: 0 10px 28px rgba(16, 42, 67, 0.08) !important;
}

.exists-card--primary {
  border-top: 4px solid #3166a5;
}

.exists-card:not(.exists-card--primary) {
  border-top: 4px solid #e1e6ef;
}

.exists-card__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  background: rgba(49, 102, 165, 0.15);
  color: #3166a5;
  margin-bottom: 10px;
}

.exists-card__step--muted {
  background: #eef1f6;
  color: #5c6b7f;
}

.exists-card__title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #1f3a63;
  margin: 0 0 8px;
}

.exists-card__text {
  margin: 0;
  font-size: 0.88rem;
  color: #4a5568;
  line-height: 1.45;
}

.exists-mail-btn {
  text-transform: none;
  letter-spacing: 0.01em;
}

.register-panel {
  background: #ffffff;
  border: 1px solid #e1e6ef;
  border-radius: 10px;
  padding: 22px;
  box-shadow: 0 8px 24px rgba(16, 42, 67, 0.06);
  border-top: 4px solid #3166a5;
}

.exists-login-heading {
  font-size: 1.15rem;
  font-weight: 600;
  color: #1f3a63;
  margin: 0 0 16px;
}

.exists-back {
  margin: 0;
  font-size: 0.92rem;
}

.exists-back-link {
  color: #3166a5;
  font-weight: 500;
  text-decoration: none;
}

.exists-back-link:hover {
  text-decoration: underline;
}
</style>
